/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../../utils/apiBaseUrl.ts';
import { getDateRangeFilter, getDateRangeEndFilter, DateRangeOption, getAvailableDateRangeOptions } from '../../utils/dateRangeUtils.ts';
import { useSalesData } from '../../contexts/SalesDataContext.tsx';
import { auth } from '../../firebase.js';
import { createPayout, updatePayout } from '../../services/payoutService.ts';
import { useOrderPaidStatus } from '../../hooks/usePayouts.ts';
import { PayoutData } from '../PayoutForm.tsx';
import { calculateOrderTotalEarned } from '../../utils/calculations.ts';
// Import sub-components
import OrderRow from './OrderRow.tsx';
import OrderTotalsExpanded from './OrderTotalsExpanded.tsx';
import OrderItemsExpanded from './OrderItemsExpanded.tsx';
import PayoutDetailsExpanded from './PayoutDetailsExpanded.tsx';
import PayoutModal from './PayoutModal.tsx';
import TagSummary from './TagSummary.tsx';
import CollectionLoadingStates from './CollectionLoadingStates.tsx';
import LoadingSpinner from '../LoadingSpinner.tsx';

import './ShopifySalesGrid.css';


interface ShopifySalesGridProps {
  tagsArray: any;
  initialSelectedValue?: number;
  onSelectionChange?: (index: number) => void;
  isAdmin?: boolean;
  authToken?: string;
  userData?: any;
}

// Helper to get a fresh auth token
const getValidAuthToken = async () => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('No authenticated user');
  }
  
  // Force token refresh - the 'true' parameter forces a refresh
  const freshToken = await currentUser.getIdToken(true);
  return freshToken;
};


const ShopifySalesGrid = ({ 
  tagsArray, 
  initialSelectedValue = 0, 
  onSelectionChange,
  isAdmin = false,
  authToken,
  userData
}: ShopifySalesGridProps) => {
  const {
    cache: cacheRef,
    selectedValue,
    setSelectedValue,
    expandedOrderTotals,
    setExpandedOrderTotals,
    expandedOrderItems,
    setExpandedOrderItems,
    hasUserMadeSelection,
    setHasUserMadeSelection,
  } = useSalesData();

  const loadingRef = useRef<Set<string>>(new Set());
  const [isLoadingCollectionData, setIsLoadingCollectionData] = useState(false);
  const [currentCollectionData, setCurrentCollectionData] = useState<any>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRangeOption>('current_month');

  const [checkedOrders, setCheckedOrders] = useState<Set<string>>(new Set());
  const [showPayoutModal, setShowPayoutModal] = useState<boolean>(false);
  const [expandedPayouts, setExpandedPayouts] = useState<Set<string>>(new Set());
  const [editingPayout, setEditingPayout] = useState<string | null>(null);
  const [isSavingPayout, setIsSavingPayout] = useState<boolean>(false);

  const currentUser = auth.currentUser;
  const currentUserId = userData?.userId || currentUser?.uid || '';
  const currentUserEmail = userData?.email || currentUser?.email || '';

  const [currentAuthUserId, setCurrentAuthUserId] = useState(auth.currentUser?.uid);

  const effectiveCreatedAt = isAdmin 
    ? '2025-01-01T00:00:00Z' 
    : (userData?.createdAt || '2025-01-01T00:00:00Z');

  // Calculate available date range options
  const availableOptions = useMemo(() => 
    getAvailableDateRangeOptions(effectiveCreatedAt),
    [effectiveCreatedAt]
  );

  // Monitor auth state changes
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentAuthUserId(user?.uid);
    });
    
    return () => unsubscribe();
  }, []);

  // Clear cache when user changes
  useEffect(() => {
    console.log('🔄 Clearing cache - Component mounted or user changed');
    cacheRef.current.clear();
    setCurrentCollectionData(null);
  }, [currentAuthUserId, cacheRef]);

  const currentOrderIds = useMemo(() => {
    if (!currentCollectionData?.orders) {
      const selectedData = tagsArray && tagsArray[selectedValue];
      const orders = selectedData?.orders || [];
      return orders.map((order: any) => `${order.orderId}`);
    }
    return currentCollectionData.orders.map((order: any) => `${order.orderId}`);
  }, [currentCollectionData, tagsArray, selectedValue]);

  const currentOrderNumbers = useMemo(() => {
    if (!currentCollectionData?.orders) {
      const selectedData = tagsArray && tagsArray[selectedValue];
      const orders = selectedData?.orders || [];
      return orders.map((order: any) => {
        let orderNum = String(order.orderNumber);
        if (orderNum.startsWith('#')) {
          orderNum = orderNum.substring(1);
        }
        return orderNum;
      });
    }
    return currentCollectionData.orders.map((order: any) => {
      let orderNum = String(order.orderNumber);
      if (orderNum.startsWith('#')) {
        orderNum = orderNum.substring(1);
      }
      return orderNum;
    });
  }, [currentCollectionData, tagsArray, selectedValue]);

  // Admins pass empty array so the service queries all payouts without a handle filter.
  // Regular users pass their collection handles so the query satisfies Firestore rules.
  const userCollectionHandles: string[] = isAdmin
    ? []
    : (userData?.collectionHandles || []);

  const { isOrderPaid, getPayoutDetails, refreshPaidStatus } = useOrderPaidStatus(
    currentOrderIds,
    currentOrderNumbers,
    userCollectionHandles
  );

  const fetchCollectionData = useCallback(async (collectionTitle: string, forceRefresh = false) => {
    const cacheKey = `${collectionTitle}_${dateRange}`;
    
    if (!forceRefresh && cacheRef.current.has(cacheKey)) {
      const cachedData = cacheRef.current.get(cacheKey);
      console.log('✅ Using cached data for:', cacheKey);
      setCurrentCollectionData(cachedData);
      setApiError(null);
      return cachedData;
    }

    if (loadingRef.current.has(cacheKey)) {
      console.log('⏳ Already loading:', cacheKey);
      return null;
    }

    console.log('📡 Fetching fresh data for:', cacheKey);
    loadingRef.current.add(cacheKey);
    setIsLoadingCollectionData(true);
    setApiError(null);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);

      console.log('📡 Making request to get-collection-sales-data-from-firestore');
      
      const freshToken = await getValidAuthToken();

      const response = await fetch(`${API_BASE_URL}/.netlify/functions/get-collection-sales-data-from-firestore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${freshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          collectionTitle: collectionTitle,
          dateRange: {
            start: getDateRangeFilter(dateRange, effectiveCreatedAt),
            end: getDateRangeEndFilter(dateRange, effectiveCreatedAt)
          }
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      console.log('📡 Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error:', errorText);
        throw new Error(`API Error: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }

      const data = await response.json();
      console.log('✅ Data received:', data);
      
      if (data.success && data.data) {
        cacheRef.current.set(cacheKey, data.data);
        setCurrentCollectionData(data.data);
        console.log('✅ Data cached and set for:', cacheKey);
        return data.data;
      } else {
        throw new Error(data.message || 'Invalid response format');
      }
    } catch (error) {
      console.error('❌ fetchCollectionData error:', error);
      let errorMessage = 'Failed to fetch collection data';
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          errorMessage = 'Request timed out. This collection may have too many orders. Please try again or contact support.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setApiError(errorMessage);
      setCurrentCollectionData(null);
      return null;
    } finally {
      loadingRef.current.delete(cacheKey);
      setIsLoadingCollectionData(false);
    }
  }, [cacheRef, dateRange, effectiveCreatedAt]);

  // Effects for initialization and data loading
  useEffect(() => {
    if (initialSelectedValue !== undefined) {
      setSelectedValue(initialSelectedValue);
      setExpandedOrderTotals(new Set());
      setExpandedOrderItems(new Set());
      setExpandedPayouts(new Set());
      setCheckedOrders(new Set());
      setHasUserMadeSelection(true);
      
      if (tagsArray && Array.isArray(tagsArray) && tagsArray[initialSelectedValue]) {
        const selectedCollection = tagsArray[initialSelectedValue];
        if (typeof selectedCollection === 'string') {
          fetchCollectionData(selectedCollection);
        }
      }
    }
  }, [initialSelectedValue, tagsArray, fetchCollectionData, setExpandedOrderItems, setExpandedOrderTotals, setSelectedValue, setHasUserMadeSelection]);

  useEffect(() => {
    const safeInitialValue = tagsArray && Array.isArray(tagsArray) && tagsArray.length > 0
      ? Math.min(initialSelectedValue, tagsArray.length - 1)
      : 0;
    
    setSelectedValue(safeInitialValue);
    setHasUserMadeSelection(true); 
    setExpandedOrderTotals(new Set());
    setExpandedOrderItems(new Set());
    setExpandedPayouts(new Set());
    setCheckedOrders(new Set());
    setCurrentCollectionData(null);
  }, [tagsArray, isAdmin, initialSelectedValue, setSelectedValue, setHasUserMadeSelection, setExpandedOrderTotals, setExpandedOrderItems, dateRange]);

  useEffect(() => {
    if (hasUserMadeSelection && tagsArray && Array.isArray(tagsArray) && tagsArray.length > 0) {
      const selectedCollection = tagsArray[selectedValue];
      
      if (typeof selectedCollection === 'string') {
        const cacheKey = `${selectedCollection}_${dateRange}`;
        
        if (cacheRef.current.has(cacheKey)) {
          const cachedData = cacheRef.current.get(cacheKey);
          console.log('✅ Using cached data for:', cacheKey);
          setCurrentCollectionData(cachedData);
          setApiError(null);
        } else {
          console.log('📡 Cache miss, fetching data for:', cacheKey);
          fetchCollectionData(selectedCollection);
        }
      } else if (selectedCollection?.tag) {
        console.log('✅ Collection is an object with tag, using directly');
        setCurrentCollectionData(selectedCollection);
      }
    }
  }, [selectedValue, isAdmin, tagsArray, hasUserMadeSelection, fetchCollectionData, cacheRef, dateRange]);

  useEffect(() => {
    if (hasUserMadeSelection && tagsArray && Array.isArray(tagsArray) && tagsArray[selectedValue]) {
      const selectedCollection = tagsArray[selectedValue];
      if (typeof selectedCollection === 'string') {
        setCurrentCollectionData(null);
        fetchCollectionData(selectedCollection, true);
      }
    }
  }, [dateRange, hasUserMadeSelection, tagsArray, selectedValue, fetchCollectionData]);

  const retryFetchCollection = useCallback(() => {
    if (isAdmin && tagsArray && tagsArray[selectedValue]) {
      const selectedCollection = tagsArray[selectedValue];
      if (typeof selectedCollection === 'string') {
        cacheRef.current.delete(selectedCollection);
        setApiError(null);
        fetchCollectionData(selectedCollection, true);
      }
    }
  }, [isAdmin, tagsArray, selectedValue, fetchCollectionData, cacheRef]);

  const currentSelection = useMemo(() => {
    if (!tagsArray || !Array.isArray(tagsArray) || tagsArray.length === 0) {
      return null;
    }

    if (currentCollectionData) {
      return {
        index: selectedValue,
        data: currentCollectionData,
        hasValidOrders: !!(currentCollectionData?.orders && Array.isArray(currentCollectionData.orders))
      };
    }

    return {
      index: selectedValue,
      data: tagsArray[selectedValue],
      hasValidOrders: false
    };
  }, [tagsArray, selectedValue, currentCollectionData]);

  const sortedOrders = useMemo(() => {
    const orders = currentCollectionData?.orders || 
                   (tagsArray && tagsArray[selectedValue]?.orders) || 
                   [];
    
    if (!Array.isArray(orders) || orders.length === 0) {
      return [];
    }
    
    return [...orders].sort((a, b) => {
      const dateA = new Date(a.orderDate || a.createdAt || 0).getTime();
      const dateB = new Date(b.orderDate || b.createdAt || 0).getTime();
      return dateB - dateA;
    });
  }, [currentCollectionData, tagsArray, selectedValue]);

  const toggleCheckbox = (orderId: string) => {
    // Refunded orders never earned real payout money — don't let them be
    // selected for a payout even if a click somehow reaches this handler.
    const order = sortedOrders.find((o: any) => String(o.orderId) === orderId);
    if (order?.financialStatus?.toLowerCase() === 'refunded') {
      return;
    }

    setCheckedOrders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const togglePayoutDetails = (orderId: string) => {
    setExpandedPayouts((prev: Set<string>) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
    setExpandedOrderTotals((prev: Set<string>) => {
      const newSet = new Set(prev);
      newSet.delete(orderId);
      return newSet;
    });
    setExpandedOrderItems((prev: Set<string>) => {
      const newSet = new Set(prev);
      newSet.delete(orderId);
      return newSet;
    });
  };

  const toggleOrderTotals = (orderId: string) => {
    setExpandedOrderTotals((prev: Set<string>) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
    setExpandedOrderItems((prev: Set<string>) => {
      const newSet = new Set(prev);
      newSet.delete(orderId);
      return newSet;
    });
    setExpandedPayouts((prev: Set<string>) => {
      const newSet = new Set(prev);
      newSet.delete(orderId);
      return newSet;
    });
  };

  const toggleOrderItems = (orderId: string) => {
    setExpandedOrderItems((prev: Set<string>) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
    setExpandedOrderTotals((prev: Set<string>) => {
      const newSet = new Set(prev);
      newSet.delete(orderId);
      return newSet;
    });
    setExpandedPayouts((prev: Set<string>) => {
      const newSet = new Set(prev);
      newSet.delete(orderId);
      return newSet;
    });
  };

  const handlePayoutSubmit = async (payoutData: PayoutData) => {
    setIsSavingPayout(true);
    try {
      const collectionHandle = typeof tagsArray[selectedValue] === 'string' 
        ? tagsArray[selectedValue]
        : tagsArray[selectedValue]?.handle || selectedTag?.handle;
      
      const collectionTitle = currentCollectionData?.tag || selectedTag?.tag;
      
      if (!payoutData.orders || payoutData.orders.length === 0) {
        throw new Error('No orders selected for payout');
      }
      
      const createdByName = userData?.firstname && userData?.lastname 
        ? `${userData.firstname} ${userData.lastname}`.trim()
        : currentUserEmail;
      
      const payoutDataToSend = {
        userId: currentUserId,
        userEmail: currentUserEmail,
        collectionTitle,
        collectionHandle: collectionHandle,
        orders: payoutData.orders,
        date: payoutData.date,
        amount: payoutData.amount,
        method: payoutData.method,
        confirmationNumber: payoutData.confirmationNumber,
        notes: payoutData.notes,
        createdBy: createdByName
      };
      
      await createPayout(payoutDataToSend);
      await refreshPaidStatus();
      setShowPayoutModal(false);
      setCheckedOrders(new Set());
      
      alert(`Payout created successfully!\n\nOrders: ${payoutData.orders.map(o => `#${o.number} ($${o.amount.toFixed(2)})`).join(', ')}\nCollection: ${collectionTitle}\nTotal Amount: $${payoutData.amount.toFixed(2)}\nConfirmation: ${payoutData.confirmationNumber}`);
      
    } catch (error) {
      console.error('❌ Full error in handlePayoutSubmit:', error);
      alert('Failed to create payout. Please try again.\n\nError: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsSavingPayout(false);
    }
  };

  const handlePayoutEdit = async (payoutData: PayoutData, payoutId: string) => {
    try {
      const updatedByName = userData?.firstname && userData?.lastname 
        ? `${userData.firstname} ${userData.lastname}`.trim()
        : currentUserEmail;
      
      await updatePayout(payoutId, {
        date: payoutData.date,
        amount: payoutData.amount,
        method: payoutData.method,
        confirmationNumber: payoutData.confirmationNumber,
        notes: payoutData.notes,
        updatedBy: updatedByName,
        updatedAt: new Date().toISOString()
      });
      
      await refreshPaidStatus();
      setEditingPayout(null);
      
      alert(`Payout updated successfully!\n\nAmount: $${payoutData.amount.toFixed(2)}\nConfirmation: ${payoutData.confirmationNumber}`);
      
    } catch (error) {
      alert('Failed to update payout. Please try again.\n\nError: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  // Handle loading/error states
  const earlyReturn = CollectionLoadingStates({
    isAdmin,
    hasUserMadeSelection,
    isLoadingCollectionData,
    tagsArray,
    apiError,
    dateRange,
    availableOptions,
    cacheSize: cacheRef.current.size,
    selectedCollectionName: tagsArray?.[selectedValue],
    onDateRangeChange: setDateRange,
    onRetry: retryFetchCollection
  });

  if (earlyReturn) {
    return earlyReturn;
  }

  if (!currentSelection) {
    return <div>Waiting for data...</div>;
  }

  const currentIndex = currentSelection.index;
  const selectedTag = currentSelection.data;
  const hasValidOrders = currentSelection.hasValidOrders;
  
  if (!selectedTag) {
    return <div>Selected tag data is not available at index {currentIndex}</div>;
  }

  const selectedOrders = selectedTag.orders?.filter((order: any) => {
    const orderIdString = String(order.orderId);
    return checkedOrders.has(orderIdString);
  }).map((order: any) => ({
    ...order,
    calculatedAmount: calculateOrderTotalEarned(order)
  })) || [];

  const selectedOrdersTotalAmount = selectedOrders.reduce((total: number, order: any) => {
    const earned = calculateOrderTotalEarned(order);
    return total + (Number(earned) || 0);
  }, 0);

  const collectionName = typeof tagsArray[selectedValue] === 'string' 
    ? tagsArray[selectedValue] 
    : tagsArray[selectedValue]?.tag || selectedTag?.tag;

  return (
    <div className="shopify-sales-grid-container">
      {isAdmin && (
        <div className="admin-mode-indicator" style={{ 
          background: '#d4edda', 
          color: '#155724', 
          padding: '8px', 
          borderRadius: '4px', 
          marginBottom: '10px',
          fontSize: '12px'
        }}>
          <strong>Admin Mode:</strong> Data is loaded on-demand per collection
          {cacheRef.current.size > 0 && (
            <span style={{ marginLeft: '10px' }}>
              ({cacheRef.current.size} collections cached)
            </span>
          )}
        </div>
      )}

      {/* Date Range Selector - Available for ALL users */}
      <div className="date-range-selector">
        <label style={{ fontWeight: 'bold', fontSize: '14px' }}>
          Date Range:
        </label>
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value as DateRangeOption)}
        >
          {availableOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {dateRange === 'all_time' && (
          <span style={{ fontSize: '12px', color: '#856404', background: '#fff3cd', padding: '4px 8px', borderRadius: '4px' }}>
            ⏳ Loading all historical data may take longer
          </span>
        )}
      </div>

      {isAdmin && (
        <div className="payout-bar">
          {checkedOrders.size > 0 && (
            <div style={{
              padding: '8px 16px',
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              fontSize: '14px'
            }}>
              <button
                onClick={() => setShowPayoutModal(true)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Create Payout ({checkedOrders.size})
              </button>
            </div>
          )}
        </div>
      )}

      <PayoutModal
        isOpen={showPayoutModal}
        isSaving={isSavingPayout}
        selectedOrders={selectedOrders}
        totalAmount={selectedOrdersTotalAmount}
        collectionName={collectionName}
        isAdmin={isAdmin}
        onClose={() => setShowPayoutModal(false)}
        onSubmit={handlePayoutSubmit}
      />

      {/* Loading indicator when switching collections */}
      {isLoadingCollectionData && (
        <div style={{
          padding: '20px',
          textAlign: 'center',
          width:'300px',
          margin:'20px auto'
        }}>
          <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'center' }}>
            <LoadingSpinner size={40} />
          </div>
          <h3 style={{ margin: '0', color: '#444444', fontSize: '18px' }}>
            Loading collection data...
          </h3>
          <p style={{ margin: '5px 0 0 0', color: '#444444', fontSize: '14px' }}>
            {selectedTag?.tag || collectionName || 'Please wait'}
          </p>
        </div>
      )}

      {/* Only show content when NOT loading */}
      {!isLoadingCollectionData && (
        <>
          <TagSummary
            selectedTag={selectedTag}
            isAdmin={isAdmin}
            calculateOrderTotalEarned={calculateOrderTotalEarned}
          />

          {/* Orders Grid */}
          {!hasValidOrders ? (
            <div className="no-orders-message" style={{ padding: '20px', background: '#f9f9f9', border: '1px solid #ddd', borderRadius: '4px' }}>
              <h3>No orders available for: {selectedTag.tag || tagsArray[selectedValue] || 'Unknown'}</h3>
            </div>
          ) : (
            <div className="sales-grid">
              <div className="grid-header desktop-only">
                <div className="header-cell">Order Number<br/><span style={{'fontSize':'80%' }}>Order ID</span></div>
                <div className="header-cell">Profit Paid</div>
                <div className="header-cell">Order Date</div>
                <div className="header-cell">Order Count</div>
                <div className="header-cell">Payment & <br/>Order Fulfillment Status</div>
                <div className="header-cell">Total Earned</div>
              </div>

              {sortedOrders.map((order: any, orderIndex: number) => {
                const orderId = String(order.orderId);
                const totalEarned = calculateOrderTotalEarned(order);
                const payoutMade = checkedOrders.has(orderId);
                const isRefunded = order.financialStatus?.toLowerCase() === 'refunded';
                
                return (
                  <React.Fragment key={orderIndex}>
                    <OrderRow
                      order={order}
                      orderId={orderId}
                      orderIndex={orderIndex}
                      totalEarned={totalEarned}
                      isAdmin={isAdmin}
                      isOrderPaid={isOrderPaid}
                      payoutMade={payoutMade}
                      expandedPayouts={expandedPayouts}
                      expandedOrderItems={expandedOrderItems}
                      expandedOrderTotals={expandedOrderTotals}
                      checkedOrdersSize={checkedOrders.size}
                      onToggleCheckbox={toggleCheckbox}
                      onTogglePayoutDetails={togglePayoutDetails}
                      onToggleOrderItems={toggleOrderItems}
                      onToggleOrderTotals={toggleOrderTotals}
                      disableCheckbox={isRefunded}
                    />

                    {expandedOrderTotals?.has(orderId) && (
                      <OrderTotalsExpanded order={order} />
                    )}

                    {expandedOrderItems?.has(orderId) && (
                      <OrderItemsExpanded order={order} />
                    )}

                    {expandedPayouts?.has(orderId) && isOrderPaid(orderId) && (
                      <PayoutDetailsExpanded
                        order={order}
                        orderId={orderId}
                        payoutData={getPayoutDetails(orderId)}
                        isAdmin={isAdmin}
                        isEditing={editingPayout === orderId}
                        onEdit={() => setEditingPayout(orderId)}
                        onCancelEdit={() => setEditingPayout(null)}
                        onSubmitEdit={(data) => handlePayoutEdit(data, getPayoutDetails(orderId).id)}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ShopifySalesGrid;
